import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  extend,
  string,
  QueryProvider,
  QueryContext,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import { CarouselHeader } from '../core/CarouselHeader';
import { getLoyaltyMembershipInput } from '../utils/helpers';
import { DiscoveryZonesPod } from './DiscoveryZonesPod';
import { useDiscoveryZoneCarouselTelemetry } from '../hooks/useDiscoveryZoneCarouselTelemetry';
import '../../styles/thd-recs-containers.scss';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string()
  })
}, DiscoveryZonesPod);

const DiscoveryZoneCarousel = (
  {
    data = null,
    parent,
    title,
    telemetryKey,
    zoneId,
    zoneSponsored,
    hideTitle = false,
    loading = false,
    scheme = 'drecs',
    showLoading = true,
    storeId,
    subtitle = null,
    membershipInformation,
    zipCode,
    hideCarouselArrows = false,
    analyticsAnchorProductSku = '',
    brandTitleMaxLine,
    mountedFn = null
  }
) => {
  const { channel } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};

  const isMobile = channel === 'mobile';

  const qtxDefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const { products } = data || {};

  useDiscoveryZoneCarouselTelemetry({ products });

  const txtPlaceholderLines = 3;
  const itemsCount = 6;
  let breakpoints = ({
    sm: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 8
    },
    md: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 16
    },
    lg: {
      slidesPerView: 6,
      slidesPerGroup: 6,
      spaceBetween: 16
    },
    xl: {
      slidesPerView: 6,
      slidesPerGroup: 6,
      spaceBetween: 16
    },
    '2xl': {
      slidesPerView: 6,
      slidesPerGroup: 6,
      spaceBetween: 16
    }
  });

  if (showLoading && loading) {
    return (
      <LoadingPlaceholder
        txtPlaceholderLines={txtPlaceholderLines}
        hideTitle={hideTitle}
        itemsCount={itemsCount}
      />
    );
  }

  return (
    <>
      {products?.length > 0 && (
        <>
          <CarouselHeader title={title} subtitle={subtitle} />
          <div className="sui-grid sui-gap-4 sui-grid-cols-1">
            <div>
              <QueryProvider
                dataSource={scheme}
                cacheKey={`${scheme}-carousel`}
                defaultVariables={qtxDefaultVariables}
                mountedFn={mountedFn}
              >
                <Carousel
                  breakpoints={breakpoints}
                  hideCarouselArrows={hideCarouselArrows}
                  disableMargin
                  containerId="discoveryZones"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(isMobile ? ({ slidesPerGroup: 2 }) : {})}
                >
                  {products.map((product, idx) => {
                    const { itemId } = product;
                    const { canonicalUrl } = product?.identifiers || {};
                    return (
                      <Wrapper key={idx}>
                        <DiscoveryZonesPod
                          itemId={itemId}
                          key={itemId}
                          position={idx}
                          storeId={storeId}
                          scheme={scheme}
                          anchorProduct={analyticsAnchorProductSku}
                          parent={parent}
                          telemetryKey={telemetryKey}
                          brandTitleMaxLine={brandTitleMaxLine}
                          zoneName={title}
                          zonePosition={zoneId}
                          zoneSponsored={zoneSponsored}
                          podResults={products?.length}
                          hideRatings={title === 'New Items to Explore'}
                          url={canonicalUrl}
                          product={product}
                        />
                      </Wrapper>
                    );
                  }
                  )}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        </>
      )}
    </>
  );
};

DiscoveryZoneCarousel.dataModel = extend(Wrapper, DiscoveryZonesPod);
DiscoveryZoneCarousel.displayName = 'DiscoveryZoneCarousel';
DiscoveryZoneCarousel.propTypes = {
  title: PropTypes.string.isRequired,
  parent: PropTypes.string.isRequired,
  telemetryKey: PropTypes.string.isRequired,
  data: PropTypes.any,
  loading: PropTypes.bool,
  hideTitle: PropTypes.bool,
  scheme: PropTypes.string,
  showLoading: PropTypes.bool,
  storeId: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  hideCarouselArrows: PropTypes.bool,
  analyticsAnchorProductSku: PropTypes.string,
  mountedFn: PropTypes.func
};

export { DiscoveryZoneCarousel };
